import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Lightbulb } from 'lucide-react';

const TopicClusters = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [topicData, setTopicData] = useState({ topics: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchTopicData();
  }, [filters]);

  const fetchTopicData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/topics?num_topics=5`);
      const result = await response.json();
      setTopicData(result);
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTopicColor = (index) => {
    const colors = ['#C9A24A', '#6B7280', '#10b981', '#f59e0b', '#ec4899'];
    return colors[index % colors.length];
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Lightbulb className="w-5 h-5 text-yellow-400" />
          Topic Clusters
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          AI-labeled conversation topics • Auto-generated from transcripts
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Clustering topics...</div>
          </div>
        ) : !topicData || !topicData.topics || topicData.topics.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No topics identified yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {topicData.topics.map((topic, index) => {
              const color = getTopicColor(index);
              
              return (
                <motion.div
                  key={index}
                  className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 hover:border-ink-200 dark:border-ink-8000 transition-colors"
                  initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: animationDuration, delay: index * 0.1 }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white"
                        style={{ backgroundColor: color }}
                      >
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-100">{topic.label}</h4>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {topic.call_count} calls ({topic.percentage}%)
                        </p>
                      </div>
                    </div>
                    <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full"
                        style={{ backgroundColor: color }}
                        initial={{ width: 0 }}
                        animate={{ width: `${topic.percentage}%` }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </div>
                  </div>

                  {/* Keywords */}
                  <div className="flex flex-wrap gap-1">
                    {topic.keywords.map((keyword, kidx) => (
                      <span
                        key={kidx}
                        className="px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: color + '20',
                          color: color
                        }}
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Stats */}
        {topicData.meta && (
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{topicData.num_topics || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Topics Found</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-400 mb-1">Method</p>
              <p className="text-sm text-slate-300 font-mono">{topicData.meta.clustering_method || 'N/A'}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{topicData.meta.sample_size || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopicClusters;
